import {
    Logger, namespace, Point, Line,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';

namespace('Ofcs.Editor.Item');

/**
 * @class DrillItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.DrillItem = AbstractItem.extend({

    type: 'drill',

    coverOptions: ['without', 'with'],

    /**
     * Available cover images for the drill item
     *
     * @property {Object} coverImages
     */
    coverImages: {
        size_60: '/bundles/ofcseditor/img/toolCover/drill-size_60.png',
        size_80: '/bundles/ofcseditor/img/toolCover/drill-size_80.png',
        // This is kept to allow old square covers to be rendered properly
        square: '/bundles/ofcseditor/img/toolCover/drill-square.png',
    },

    /**
     * Allowed sizes for the drill item
     *
     * @property {Array.<number>} defaultSizes
     */
    defaultSizes: [60, 80],

    /**
     * Constant for minimum diameter of a circle bag
     *
     * @constant
     * @property {number} MIN_SIZE
     */
    MIN_SIZE: 60,

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {DrillItem} item Me
     */

    defaults: {
        /**
         * @property {string} Type
         */
        type: 'Drill',

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Width of the shape
         * @property {number} attributes.width
         */
        width: 80,

        /**
         * Height of the shape
         * @property {number} attributes.height
         */
        height: 80,

        /**
         * Label
         * @property {string} attributes.userLabel
         */
        userLabel: '',

        /**
         * Rotation of the shape
         * @property {number} attributes.rotation
         */
        rotation: 0,

        /**
         * Path to item image
         * @property {string} attributes.image
         */
        image: null,

        /**
         * Current snap index
         * @see ResizeDrillHandle
         * @property {string} attributes.snapIndex
         */
        snapIndex: 1,

        /**
         * Cover status
         * Possible values: without, with, square
         * @property {string} attributes.cover
         */
        cover: 'without',

        /**
         * Covered area
         * Will be set by tabletopCollisionManager
         * @property {number} attributes.area
         */
        area: 0,

        /**
         * Defines it item is billable
         * Will be set and checked by tabletopCollisionManager
         * @property {Boolean} attributes.billable
         */
        billable: true,
    },

    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.supportsWidth = true;
        this.supportsPositioning = true;
        this.supportsDepth = false;
        this.supportsCollisions = true;
        this.supportsOutOfFoam = true;
        this.supportsGuides = true;
        this.supportsInsideTable = 'inside';
        this.supportsBidirectionalCollisions = true;
        this.supportsCover = true;

        this.marginSizes = new Map([
            [60, 20],
            [80, 23.5],
        ]);

        // Initialize paths
        this.set('ext', this.calculateSegments(0));
        this.set('orig', this.get('ext'));
        this.set('safety', this.calculateSegments(this.getSafetyMargin()));

        this.on('change', this.onItemChange, this);
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        const height = this.get('height');
        return this.getMarginSize(height);
    },

    /**
     * Return the margin size according to the diameter of the drill circle
     *
     * @param {number} height The height of the circle
     * @return {number}
     */
    getMarginSize(height) {
        return this.marginSizes.has(height) ? this.marginSizes.get(height) : 20;
    },

    onItemChange(item) {
        const spread = this.getSafetyMargin();

        if (this.hasChangedProperty(item, ['width', 'height'])) {
            this.set('ext', this.calculateSegments(0));
            this.set('orig', this.get('ext'));
            this.set('safety', this.calculateSegments(spread));
            this.trigger('updatesegments', this);
        }
    },

    calculateSegments(spread) {
        const radius = (this.get('height') / 2) + (spread);
        const path = [];
        let step = Math.round(720 / (radius));

        step = Math.max(1, Math.min(10, step));

        for (let angle = 0; angle < 360; angle += step) {
            const x = radius * Math.cos((angle * Math.PI) / 180);
            const y = radius * Math.sin((angle * Math.PI) / 180);

            if (!angle) {
                path.push(['M', x, y]);
            } else {
                path.push(['L', x, y]);
            }
        }

        path.push(['L', radius, 0]);

        return [path];
    },

    set(key, value, optionsParam) {
        let attrs = {};
        let options = optionsParam;

        // Handle both `"key", value` and `{key: value}` -style arguments.
        if (typeof key === 'object') {
            attrs = key;
            options = value;
        } else {
            attrs[key] = value;
        }

        options = options || {};

        if (attrs.width !== undefined) {
            attrs.width = Math.max(this.MIN_SIZE, attrs.width);
            Logger.debug('drill: fixed width', attrs.width);

            if (attrs.height === undefined || attrs.height === this.attributes.height) {
                attrs.height = attrs.width;
            }
        }

        if (attrs.height !== undefined) {
            attrs.height = Math.max(this.MIN_SIZE, attrs.height);
            Logger.debug('drill: fixed height', attrs.height);

            if (attrs.width === undefined || attrs.width === this.attributes.width) {
                attrs.width = attrs.height;
            }
        }

        return AbstractItem.prototype.set.call(this, attrs, options);
    },

    /**
     * Get radius.
     * @param {boolean} withMargin - if true returns radius incl. margin
     * @return {number} radius
     */
    getRadius(withMargin) {
        const height = this.get('height');
        if (withMargin) {
            return (height / 2) + this.getMarginSize(height);
        }
        return height / 2;
    },

    /**
     * Get top point.
     *
     * @param {boolean} withMargin Indicates if margin should be used in calculation
     * @return {Point}
     */
    getPointTop(withMargin) {
        const center = this.get('position');
        const height = this.get('height');
        const radius = withMargin ? (height / 2) + this.getMarginSize(height) : height / 2;

        return new Point(center.x, center.y - radius);
    },

    /**
     * Get bottom point.
     *
     * @param {boolean} withMargin Indicates if margin should be used in calculation
     * @return {Point}
     */
    getPointBottom(withMargin) {
        const center = this.get('position');
        const height = this.get('height');
        const radius = withMargin ? (height / 2) + this.getMarginSize(height) : height / 2;

        return new Point(center.x, center.y + radius);
    },

    /**
     * Get left point.
     *
     * @param {boolean} withMargin Indicates if margin should be used in calculation
     * @return {Point}
     */
    getPointLeft(withMargin) {
        const center = this.get('position');
        const width = this.get('width');
        const radius = withMargin ? (width / 2) + this.getMarginSize(width) : width / 2;

        return new Point(center.x - radius, center.y);
    },

    /**
     * Get right point.
     *
     * @param {boolean} withMargin Indicates if margin should be used in calculation
     * @return {Point}
     */
    getPointRight(withMargin) {
        const center = this.get('position');
        const width = this.get('width');
        const radius = withMargin ? (width / 2) + this.getMarginSize(width) : width / 2;

        return new Point(center.x + radius, center.y);
    },

    /**
     * Get top-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopLeft(withMargin) {
        const { y } = this.getPointTop(withMargin);
        const { x } = this.getPointLeft(withMargin);

        return new Point(x, y);
    },

    /**
     * Get top-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopRight(withMargin) {
        const { y } = this.getPointTop(withMargin);
        const { x } = this.getPointRight(withMargin);

        return new Point(x, y);
    },

    /**
     * Get bottom-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomLeft(withMargin) {
        const { y } = this.getPointBottom(withMargin);
        const { x } = this.getPointLeft(withMargin);

        return new Point(x, y);
    },

    /**
     * Get bottom-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomRight(withMargin) {
        const { y } = this.getPointBottom(withMargin);
        const { x } = this.getPointRight(withMargin);

        return new Point(x, y);
    },

    /**
     * Get minimum with.
     *
     * @return {number}
     */
    getMinWidth() {
        return this.MIN_SIZE;
    },

    /**
     * Get minimum height.
     *
     * @return {number}
     */
    getMinHeight() {
        return this.MIN_SIZE;
    },

    /**
     * Get the area of the item
     *
     * @param {Point} point - possible intersection point
     * @param {Point} otherPoint - possible second intersection point
     * @return {number}
     */
    getArea(point, otherPoint) {
        const radius = this.getRadius();

        if ((!point && !otherPoint) || (point && !otherPoint)) {
            return (radius * radius * Math.PI);
        }
        const center = this.get('position');

        const line1 = new Line(point, center);
        const line2 = new Line(center, otherPoint);
        const angle = line1.getAngleToLine(line2);

        return ((radius * radius) / 2) * (((Math.PI * angle) / 180) - Math.sin((angle * Math.PI) / 180));
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, args);

        return {
            x: attributes.x,
            y: attributes.y,
            width: attributes.width,
            height: attributes.height,
            type: attributes.type,
            cover: attributes.cover,
            billable: attributes.billable,
        };
    },

    /**
     * Initializes item handles
     *
     * @param {Canvas} canvas - svg canvas instance
     * @return {ResizeDrillHandle}
     */
    initializeHandles(canvas) {
        return new Ofcs.Editor.Svg.Handle.ResizeDrillHandle(this, canvas);
    },
});
